import { inject } from '@angular/core';
import { signalStore, withState, withMethods, patchState } from '@ngrx/signals';

import { AuthService } from './auth.service';

import type { AuthUser } from './auth.models';

type AuthState = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
};

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState<AuthState>(initialState),
  withMethods((store, authService = inject(AuthService)) => {
    // 並列の checkAuthStatus を 1 つの Promise に集約し、
    // Amplify SDK 呼び出しと patchState の競合を防ぐ。
    let checkAuthInFlight: Promise<void> | null = null;

    const runCheckAuthStatus = async (): Promise<void> => {
      patchState(store, { loading: true, error: null });
      try {
        const session = await authService.fetchSession();
        if (session.tokens) {
          const user = await authService.getCurrentUser();
          patchState(store, {
            user: {
              email: user.signInDetails?.loginId ?? null,
              userId: user.userId,
            },
            isAuthenticated: true,
            loading: false,
            error: null,
          });
        } else {
          patchState(store, {
            user: null,
            isAuthenticated: false,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '認証状態の確認に失敗しました';
        patchState(store, {
          user: null,
          isAuthenticated: false,
          loading: false,
          error: errorMessage,
        });
      }
    };

    return {
      checkAuthStatus(): Promise<void> {
        if (checkAuthInFlight) {
          return checkAuthInFlight;
        }
        checkAuthInFlight = runCheckAuthStatus().finally(() => {
          checkAuthInFlight = null;
        });
        return checkAuthInFlight;
      },

      async logout(): Promise<void> {
        patchState(store, { loading: true, error: null });
        try {
          await authService.signOut();
          patchState(store, {
            user: null,
            isAuthenticated: false,
            loading: false,
          });
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'サインアウトに失敗しました';
          patchState(store, { error: errorMessage, loading: false });
        }
      },

      setError(error: string): void {
        patchState(store, { error, loading: false });
      },
    };
  }),
);
